
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { handleLinkedInCallback } from '../../utils/linkedinApi';
import { toast } from 'react-toastify';
import LoadingPage from '../LoadingPage';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

const LinkedInCallback: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { authUser } = useAuth();
    const [processing, setProcessing] = useState(true);

    useEffect(() => {
        const processCallback = async () => {
            const code = searchParams.get('code');
            const state = searchParams.get('state');
            const error = searchParams.get('error');

            if (error) {
                toast.error(`LinkedIn connection failed: ${error}`);
                navigate('/creators'); // Or settings
                return;
            }

            if (!code || !state) {
                toast.error("Invalid callback parameters");
                navigate('/creators');
                return;
            }

            if (!authUser?.uid) {
                // Wait for auth or redirect to login
                // If auth is loading, we might need to wait, but this component mounts after app init usually
                // For now assumes authUser is present or we wait for simple check
                return;
            }

            try {
                const response = await handleLinkedInCallback(code, state, authUser.uid);
                if (response.ok) {
                    toast.success("LinkedIn account connected successfully!");
                    navigate(`/${authUser.userName}/settings`);
                } else {
                    const data = await response.json();
                    toast.error(data.message || "Failed to connect LinkedIn account");
                    navigate(`/${authUser.userName}/settings`);
                }
            } catch (err) {
                console.error("LinkedIn callback error:", err);
                toast.error("An error occurred while connecting LinkedIn");
                navigate(`/${authUser.userName}/settings`);
            } finally {
                setProcessing(false);
            }
        };

        if (authUser) {
            processCallback();
        } else if (authUser === null) {
            // Not logged in?
            // Maybe store code/state in localstorage and redirect to login?
            // For now, assume user is logged in as they initiated flow.
            // If completely logged out, we might want to redirect to signin
        }
    }, [searchParams, navigate, authUser]);

    if (!authUser) {
        // Optionally render loading or redirect
        return <LoadingPage />;
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 2 }}>
            <LoadingPage />
            <Typography variant="h6">Connecting your LinkedIn account...</Typography>
        </Box>
    );
};

export default LinkedInCallback;
